import React from 'react';
import AttributeTable from '../AttributeTable';
import SectionView from '../SectionView';
import { BoreholeSectionOverlay } from '../boreholes/BoreholeColumn';
import VariogramEditor from '../VariogramEditor';
import ChartPanel from '../ChartPanel';
import GridProfilePanel from '../GridProfilePanel';
import PseudosectionPanel from '../pseudosection/PseudosectionPanel';
import { gridResultLayers } from '../../lib/grid-ops';

/**
 * Elements for the bottom-dock panels, keyed by panel-registry id; a closed panel is null.
 */
export function bottomPanelContent({ showGridProfile, setShowGridProfile, setDrawMode, clearDrawn, importedLayers, showAttributeTable, setShowAttributeTable, showChartPanel, setShowChartPanel, showVariogram, setShowVariogram, showSectionView, setShowSectionView, pseudosection, setPseudosection, inversionModels, sectionModelId, setSectionModelId, resultLayers, setResultLayers, chartFilter, setChartFilter, chartableLayers, activeLayerKey, xyzStats, toggleExcludeRow, lineData, activeXyzLayer, activeTableLayer, drawnFeatures, selectedFeature, setSelectedFeature, setSelectedAnomaly, showXyzPopup, chartSelection }) {
  return {
      // Attribute table — shows the active XYZ layer when present 
      attributes: showAttributeTable ? (activeXyzLayer ? (
        <AttributeTable
          features={activeXyzLayer.geojson.features}
          layerName={activeXyzLayer.name}
          zFields={activeXyzLayer.xyz.zFields}
          activeZ={activeXyzLayer.activeZ}
          zStats={xyzStats[activeXyzLayer.id]}
          onToggleExclude={(rowIdx) => toggleExcludeRow(activeXyzLayer.id, rowIdx)}
          onFeatureClick={(f) => {
            if (f.geometry?.type === 'Point') {
              showXyzPopup({
                longitude: f.geometry.coordinates[0], latitude: f.geometry.coordinates[1],
                properties: f.properties, zFields: activeXyzLayer.xyz.zFields,
              });
            }
          }}
          onClose={() => setShowAttributeTable(false)}
        />
      ) : activeTableLayer ? (
        <AttributeTable
          features={activeTableLayer.features}
          layerName={activeTableLayer.name}
          onFeatureClick={(f) => {
            if (f.geometry?.type === 'Point') {
              showXyzPopup({ longitude: f.geometry.coordinates[0], latitude: f.geometry.coordinates[1], properties: f.properties, zFields: [] });
            }
          }}
          onClose={() => setShowAttributeTable(false)}
        />
      ) : (
        <AttributeTable
          features={lineData.features}
          layerName="Survey Lines"
          onFeatureClick={(f) => {
            if (f.properties) {
              setSelectedFeature({ longitude: f.geometry.coordinates[0][0], latitude: f.geometry.coordinates[0][1], ...f.properties });
              setSelectedAnomaly(null);
            }
          }}
          selectedFeatureId={selectedFeature?.id}
          onClose={() => setShowAttributeTable(false)}
        />
      )) : null,

      // Section / profile view — bottom panel 
      section: showSectionView ? (
        <SectionView
          key={sectionModelId || 'profiles'}
          gridLayers={gridResultLayers(resultLayers)}
          drawnLines={drawnFeatures.filter(f => f.geometry?.type === 'LineString')}
          inversionModels={inversionModels}
          initialModelId={sectionModelId}
          onClose={() => { setShowSectionView(false); setSectionModelId(null); }}
          renderOverlay={(g) => <BoreholeSectionOverlay {...g} />}
        />
      ) : null,

      // Pseudosection / data fit — bottom panel (components/pseudosection) 
      pseudosection: pseudosection ? (
        <PseudosectionPanel
          key={`${pseudosection.datasetId || ''}|${pseudosection.modelId || pseudosection.name || ''}`}
          {...pseudosection}
          inversionModels={inversionModels}
          onClose={() => setPseudosection(null)}
        />
      ) : null,

      // Variogram editor — bottom panel 
      variogram: showVariogram ? (
        <VariogramEditor
          layers={[
            ...chartableLayers,
            ...(drawnFeatures.length > 0 ? [{ id: 'drawn', name: 'Drawn Features', geojson: { type: 'FeatureCollection', features: drawnFeatures } }] : []),
          ]}
          onResult={(geojson, name, kind) => {
            setResultLayers(prev => [...prev, { id: `result-${Date.now()}`, name, geojson, kind: kind || 'grid', visible: true }]);
          }}
          onClose={() => setShowVariogram(false)}
        />
      ) : null,

      gridProfile: showGridProfile ? (
        <GridProfilePanel
          gridLayers={gridResultLayers(resultLayers)}
          pointLayers={importedLayers}
          drawnFeatures={drawnFeatures}
          setDrawMode={setDrawMode}
          clearDrawn={clearDrawn}
          onClose={() => { setShowGridProfile(false); setDrawMode(null); }}
        />
      ) : null,

      // Chart panel — linked to map (G35) 
      charts: showChartPanel ? (
        <ChartPanel
          layers={chartableLayers}
          activeFilter={chartFilter}
          onFilter={setChartFilter}
          mapSelection={chartSelection}
          activeLayerKey={activeLayerKey}
          onClose={() => { setShowChartPanel(false); setChartFilter(null); }}
        />
      ) : null,
  };
}
